import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Toaster } from '../components/ui/toaster';
import { 
  ArrowRight, ArrowLeft, CheckCircle, Building2, Cloud, Zap,
  Shield, TrendingUp, Users, Layers, Globe, Sparkles,
  BarChart3, Workflow, Lock, Rocket, Target, Settings
} from 'lucide-react';

const solutionData = {
  enterprise: {
    title: 'Enterprise Solutions',
    subtitle: 'Scale Your Business Operations',
    description: 'Comprehensive enterprise-grade solutions designed to streamline operations, enhance productivity, and drive sustainable growth across your organization.',
    icon: Building2,
    color: 'from-blue-500 to-indigo-600',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80',
    offerings: [
      { name: 'ERP Integration', desc: 'Seamless integration of enterprise resource planning systems', icon: Workflow },
      { name: 'Business Process Automation', desc: 'Automate repetitive tasks and workflows', icon: Settings },
      { name: 'Custom Enterprise Applications', desc: 'Tailored applications for your unique needs', icon: Layers },
      { name: 'Legacy System Modernization', desc: 'Upgrade outdated systems to modern platforms', icon: Rocket },
      { name: 'Data Analytics & BI', desc: 'Transform data into actionable insights', icon: BarChart3 },
      { name: 'Enterprise Security', desc: 'Protect your critical business assets', icon: Lock }
    ],
    benefits: [
      { title: 'Improved Efficiency', desc: 'Reduce operational costs by up to 40%' },
      { title: 'Better Decision Making', desc: 'Real-time insights for strategic decisions' },
      { title: 'Enhanced Collaboration', desc: 'Break down silos across departments' },
      { title: 'Scalable Growth', desc: 'Solutions that grow with your business' }
    ],
    useCases: [
      'Manufacturing & Supply Chain',
      'Financial Services',
      'Healthcare Organizations',
      'Retail & Distribution'
    ]
  },
  cloud: {
    title: 'Cloud Solutions',
    subtitle: 'Accelerate Your Cloud Journey',
    description: 'Transform your infrastructure with secure, scalable, and cost-effective cloud solutions. We help you harness the full potential of cloud computing.',
    icon: Cloud,
    color: 'from-sky-500 to-cyan-600',
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80',
    offerings: [
      { name: 'Cloud Migration', desc: 'Seamless migration to cloud platforms', icon: Rocket },
      { name: 'Multi-Cloud Strategy', desc: 'Leverage best of multiple cloud providers', icon: Globe },
      { name: 'Cloud Security', desc: 'Enterprise-grade security for cloud workloads', icon: Shield },
      { name: 'Cloud Optimization', desc: 'Reduce costs and improve performance', icon: TrendingUp },
      { name: 'Hybrid Cloud', desc: 'Balance on-premise and cloud infrastructure', icon: Layers },
      { name: 'Cloud-Native Development', desc: 'Build applications designed for the cloud', icon: Settings }
    ],
    benefits: [
      { title: 'Cost Reduction', desc: 'Up to 30% savings on infrastructure costs' },
      { title: 'High Availability', desc: '99.9% uptime with redundant systems' },
      { title: 'Global Scalability', desc: 'Deploy worldwide in minutes' },
      { title: 'Enhanced Security', desc: 'Advanced threat protection included' }
    ],
    useCases: [
      'SaaS Applications',
      'E-commerce Platforms',
      'Data Analytics Workloads',
      'Disaster Recovery'
    ]
  },
  digital: {
    title: 'Digital Transformation',
    subtitle: 'Reimagine Your Business',
    description: 'Embrace digital innovation to stay competitive. Our digital transformation solutions help you reinvent business models and create exceptional experiences.',
    icon: Zap,
    color: 'from-purple-500 to-violet-600',
    image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&q=80',
    offerings: [
      { name: 'Process Digitization', desc: 'Convert manual processes to digital workflows', icon: Workflow },
      { name: 'AI & Machine Learning', desc: 'Intelligent automation and predictions', icon: Target },
      { name: 'Customer Experience', desc: 'Create seamless digital experiences', icon: Users },
      { name: 'Data-Driven Insights', desc: 'Analytics for better decision making', icon: BarChart3 },
      { name: 'IoT Integration', desc: 'Connect devices and gather insights', icon: Globe },
      { name: 'Digital Workplace', desc: 'Enable modern workforce collaboration', icon: Building2 }
    ],
    benefits: [
      { title: 'Competitive Advantage', desc: 'Stay ahead of digital-first competitors' },
      { title: 'Customer Satisfaction', desc: 'Deliver exceptional digital experiences' },
      { title: 'Innovation Culture', desc: 'Foster continuous improvement' },
      { title: 'Revenue Growth', desc: 'New digital revenue streams' }
    ],
    useCases: [
      'Customer Engagement',
      'Supply Chain Optimization',
      'Smart Manufacturing',
      'Digital Banking'
    ]
  }
};

const SolutionDetailPage = () => {
  const { solutionId } = useParams();
  const pageRef = useRef(null);
  const solution = solutionData[solutionId] || solutionData.enterprise;
  const IconComponent = solution.icon;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = pageRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [solutionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50/30" ref={pageRef}>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-28 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="hero-orb hero-orb-1 opacity-40"></div>
            <div className="hero-orb hero-orb-2 opacity-30"></div>
            <div className="absolute inset-0 dot-grid opacity-20"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <Link to="/solutions" className="inline-flex items-center gap-2 text-gray-500 hover:text-[#ff6b35] mb-8 reveal">
              <ArrowLeft className="w-4 h-4" />
              Back to Solutions
            </Link>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="reveal-left">
                <div className={`inline-flex items-center gap-3 px-4 py-2 bg-gradient-to-r ${solution.color} text-white rounded-full shadow-lg mb-6`}>
                  <IconComponent className="w-5 h-5" />
                  <span className="text-sm font-medium">{solution.subtitle}</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                  {solution.title}
                </h1>

                <p className="text-lg text-gray-600 leading-relaxed mb-8">
                  {solution.description}
                </p>

                <div className="flex flex-wrap gap-4">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-3 bg-gradient-to-r from-[#ff6b35] to-[#ff8f5c] text-white px-8 py-4 rounded-full font-semibold shadow-xl shadow-[#ff6b35]/30 hover:shadow-[#ff6b35]/50 hover:scale-[1.02] transition-all duration-300"
                  >
                    Get Started
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>

              <div className="reveal-right relative">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src={solution.image} 
                    alt={solution.title}
                    className="w-full h-[400px] object-cover"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${solution.color} opacity-20`}></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Offerings */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16 reveal">
              <span className="inline-block px-4 py-2 bg-orange-50 text-[#ff6b35] rounded-full text-sm font-semibold mb-4">
                What We Offer
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Our Offerings
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {solution.offerings.map((item, index) => (
                <div 
                  key={item.name}
                  className="reveal-scale bento-card group cursor-pointer hover:border-[#ff6b35]/30"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${solution.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{item.name}</h3>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="reveal-left">
                <span className="inline-block px-4 py-2 bg-white text-[#ff6b35] rounded-full text-sm font-semibold shadow-sm mb-4">
                  Benefits
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                  Why Choose This Solution
                </h2>

                <div className="space-y-6">
                  {solution.benefits.map((benefit, index) => (
                    <div key={benefit.title} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">{benefit.title}</h3>
                        <p className="text-gray-600 text-sm">{benefit.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="reveal-right">
                <div className="bento-card bg-gray-900">
                  <h3 className="text-white font-semibold text-lg mb-6">Ideal For</h3>
                  <ul className="space-y-4">
                    {solution.useCases.map((useCase) => (
                      <li key={useCase} className="flex items-center gap-3 text-gray-300">
                        <div className="w-2 h-2 rounded-full bg-[#ff6b35]"></div>
                        {useCase}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-[#ff6b35] to-[#ff8f5c] relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30"></div>
          
          <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <Sparkles className="w-12 h-12 text-white/80 mx-auto mb-6 reveal" />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 reveal">
              Ready to Transform?
            </h2>
            <p className="text-white/90 text-lg mb-10 reveal">
              Let's discuss how our {solution.title.toLowerCase()} can help your business thrive.
            </p>
            <Link 
              to="/contact"
              className="reveal inline-flex items-center gap-3 bg-white text-[#ff6b35] px-8 py-4 rounded-full font-semibold shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
            >
              Schedule a Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
};

export default SolutionDetailPage;
